import { useRef, useLayoutEffect, useEffect, useSyncExternalStore } from "react";
import { RealtimeUserClient } from "./realtime-user-client";
import { RealtimeClient } from "./realtime-client";
import { MockNatsAdapter } from "./mock-nats-adapter";
import { NatsAdapter, NatsError } from "./nats-adapter";
import {
  getClientNetworkState,
  getEverConnectedOnThisDevice,
  markEverConnectedOnThisDevice,
  getLastConnectedBucket,
  getDeviceFlagAgeBucket,
  ensureFlagCreatedAt,
} from "./types";
import type { RealtimePayload, RealtimeMessageHandler, RealtimeMetrics } from "./types";
import type { IRealtimeUserClient } from "./realtime-user-client";
import { addBreadcrumb, captureException as defaultCaptureException, withScope } from "@web-monorepo/telemetry";

const RECOVERY_BASE_DELAY_MS = 5_000;
const RECOVERY_JITTER_MS = 10_000;
const MAX_RECOVERY_ATTEMPTS = 3;
// Post-exhaustion slow-retry backoff: full jitter, cap doubles from base to max — same
// full-jitter idiom as the adapter's own reconnect backoff.
const SLOW_RETRY_BASE_MS = 60_000;
const SLOW_RETRY_MAX_MS = 300_000;
// E1 poll fallback: while exhausted, refetch on this cadence so consumers degrade to
// "data is up to ~2 min stale" instead of fully dead.
const POLL_INTERVAL_MS = 90_000;
const POLL_JITTER_MS = 30_000;
// On flaky networks, rebuildClient() (which calls notifyReconnect) is often
// followed milliseconds later by the rebuilt connection's adapter-level
// "reconnect" status (which calls notifyReconnect again via
// handleReconnectSuccess). Each fan-out triggers consumers' broad cache
// invalidations (messageThread/messages/scheduledMessages/sidebar unreads) —
// firing twice in quick succession produces a visible refetch storm.
// Trailing-edge debounce so the cascade fires once after the burst settles.
const RECONNECT_NOTIFY_DEBOUNCE_MS = 500;

interface CreateRealtimeHookOptions {
  serverUrl: string;
  name: string;
  mockAdapter?: MockNatsAdapter | null;
  onError?: () => void;
  onConnectionExhausted?: () => void;
  onInitError?: () => void;
  // Fires when MAX_RECOVERY_ATTEMPTS is crossed. May fire multiple times if
  // recoveryAttempts is reset (successful reconnect or visibility-change cycle)
  // and the connection exhausts again. Also fires on each subsequent
  // scheduleRecovery() while still in the exhausted state, AND now on each failed
  // slow retry while the post-exhaustion machine runs (a cycle can now last hours) —
  // consumers should dedupe themselves if they're doing non-idempotent work.
  onRecoveryExhausted?: () => void;
  // Gates the post-exhaustion state machine (slow re-dial + auto-dismiss).
  // Evaluated lazily at exhaustion time (not at hook creation) so it can read a per-user
  // feature switch that loads after module init. When absent or returns false, exhaustion
  // is a dead-end exactly as before (feature switch OFF = today's behavior).
  isExhaustedStateMachineEnabled?: () => boolean;
  // Gates the E1 poll-while-exhausted refetch loop, independently of the machine switch —
  // the poll is the one piece with real API-load risk, so killing it must not kill the slow
  // re-dial (kiosk recovery). Only ever evaluated when isExhaustedStateMachineEnabled is on;
  // lazy for the same reason. Absent or false = no polling.
  isExhaustedPollEnabled?: () => boolean;
  // Fired when the client recovers from a state where the realtime-sync banner could be
  // showing (i.e. after onRecoveryExhausted fired). Apps wire this to hideErrorBanner so the
  // banner auto-dismisses on recovery. Does NOT fire on the first healthy connect of a session.
  onRecovered?: () => void;
  metrics?: RealtimeMetrics;
  // Injectable telemetry sink (defaults to the real @web-monorepo/telemetry captureException).
  // Injected like `metrics` so tests observe captures via a plain callback instead of spying
  // on the module export.
  captureException?: typeof defaultCaptureException;
}

export function createRealtimeHook(options: CreateRealtimeHookOptions) {
  const captureException = options.captureException ?? defaultCaptureException;
  let realtimeClient: RealtimeClient | null = null;
  const topicClients = new Map<string, IRealtimeUserClient>();
  const topicSubscriberCounts = new Map<string, number>();
  let currentEntityId: string | null = null;
  let initPromise: Promise<void> | null = null;
  let hadSubscriptionError = false;
  let errorResetTimer: ReturnType<typeof setTimeout> | undefined;

  const ERROR_FLAG_TTL_MS = 30_000;

  let connectionExhausted = false;
  let recoveryTimer: ReturnType<typeof setTimeout> | undefined;
  let recoveryAttempts = 0;
  // Once recoveryAttempts hits MAX_RECOVERY_ATTEMPTS, every subsequent
  // scheduleRecovery() call would re-fire captureException — producing 5×
  // amplification per affected user. Gate the Sentry capture (only) so it
  // fires once per exhaustion cycle; onRecoveryExhausted itself stays
  // ungated so the public callback contract is unchanged. Reset alongside
  // recoveryAttempts whenever the user gets a fresh attempt budget.
  let sentryExhaustedCaptured = false;

  // Post-exhaustion state machine (A4/A5/E1): once the fast ladder exhausts, these track
  // the slow re-dial + poll fallback loop that replaces the old dead-end. slowRetryAttempts
  // grows the backoff cap independently of recoveryAttempts, which stays pinned at
  // MAX_RECOVERY_ATTEMPTS while exhausted (slow rebuilds never burn the fast-ladder budget).
  let inExhaustedState = false;
  let slowRetryAttempts = 0;
  let slowRetryTimer: ReturnType<typeof setTimeout> | undefined;
  let pollTimer: ReturnType<typeof setTimeout> | undefined;
  // True once onRecoveryExhausted has fired since the last successful connect — i.e. the
  // banner could be showing. connectionExhausted is NOT a reliable proxy for this: rebuildClient
  // clears it before the connect resolves, so it flips back to false well before onRecovered
  // should fire.
  let recoveryExhaustedNotified = false;

  // Bumped by close(). A rebuildClient() that was mid-await when close() ran must
  // abandon its resume — otherwise it would bump the reconnect generation and
  // resurrect a client on the closed (e.g. logged-out) session's dead auth.
  let closeGeneration = 0;

  // Page-load-lifetime flag: did ANY adapter (including rebuilds from rebuildClient)
  // complete a connect since this hook instance was created? Lives on the hook, not the
  // adapter, because the adapter's own "ever connected" state resets on every rebuild —
  // this needs to survive across rebuilds to answer "did realtime work at all this session".
  let everConnectedThisSession = false;

  let reconnectGeneration = 0;
  const reconnectListeners = new Set<() => void>();

  function subscribeReconnect(listener: () => void) {
    reconnectListeners.add(listener);
    return () => {
      reconnectListeners.delete(listener);
    };
  }

  function getReconnectSnapshot() {
    return reconnectGeneration;
  }

  function bumpReconnectGeneration() {
    reconnectGeneration++;
    for (const listener of reconnectListeners) {
      listener();
    }
  }

  // Consumers that need to refetch state after a reconnect (NATS core is fire-and-forget,
  // so any events published while the WebSocket was disconnected are permanently lost).
  // Fires on both transient reconnects (adapter onReconnect) and exhausted-recovery rebuilds.
  // Listener receives `disconnectMs` (elapsed time since the first preceding disconnect)
  // so it can skip refetches for sub-second flaps where no events were realistically lost.
  // Recovery rebuilds pass `Infinity` to indicate the connection was effectively dead.
  const refetchListeners = new Set<(disconnectMs: number) => void>();
  let reconnectNotifyTimer: ReturnType<typeof setTimeout> | undefined;
  let pendingDisconnectMs = 0;

  function addReconnectListener(listener: (disconnectMs: number) => void): () => void {
    refetchListeners.add(listener);
    return () => {
      refetchListeners.delete(listener);
    };
  }

  function notifyReconnect(disconnectMs: number) {
    // Coalesce concurrent reconnects (adapter event + recovery rebuild) on the
    // longest observed gap so consumers gating on duration see the worst case.
    pendingDisconnectMs = Math.max(pendingDisconnectMs, disconnectMs);
    clearTimeout(reconnectNotifyTimer);
    reconnectNotifyTimer = setTimeout(() => {
      reconnectNotifyTimer = undefined;
      const dispatchedMs = pendingDisconnectMs;
      pendingDisconnectMs = 0;
      for (const listener of refetchListeners) {
        listener(dispatchedMs);
      }
    }, RECONNECT_NOTIFY_DEBOUNCE_MS);
  }

  // Record a successful connect. Call markEverConnectedOnThisDevice() on every connect/
  // reconnect, not just the first of this page-load lifetime: kiosk-style sessions can run
  // for days, reconnecting daily, and a page-load-scoped gate would mean the stored
  // timestamp never refreshes — drifting the device into a stale bucket (over_30d) despite
  // connecting fine every day. The 24h write throttle now lives inside the helper itself.
  function markConnectedThisSession() {
    markEverConnectedOnThisDevice();
    everConnectedThisSession = true;
  }

  function handleReconnectSuccess(disconnectMs: number) {
    const wasBannerShowing = recoveryExhaustedNotified;
    addBreadcrumb({
      category: "nats",
      message: "Reconnected - resetting recovery attempts to 0",
      level: "info",
      data: { name: options.name, disconnectMs },
    });
    recoveryAttempts = 0;
    sentryExhaustedCaptured = false;
    markConnectedThisSession();
    notifyReconnect(disconnectMs);
    exitExhaustedState();
    if (wasBannerShowing) {
      recoveryExhaustedNotified = false;
      options.onRecovered?.();
    }
  }

  function handleConnectSuccess() {
    // Refund the recovery budget when a fresh adapter completes its initial connect.
    // This is the symmetric counterpart to handleReconnectSuccess — the latter fires
    // on the adapter's "reconnect" status (transient reconnect of the same connection),
    // while this fires on a confirmed initial connect of a NEW adapter (built by
    // rebuildClient). Without this, every successful rebuild is a one-way budget
    // decrement — the 4th lights the banner even though all 4 reconnected fine (NOTF-3).
    // Do NOT call notifyReconnect here for fast-ladder rebuilds — rebuildClient already
    // fired it with Infinity (notify: true) to trigger the refetch fan-out. Slow-phase
    // rebuilds are the exception: they defer the fan-out (notify: false) until the connect
    // actually lands — i.e. here — so consumers refetch what they missed while exhausted.
    // Gate the breadcrumb on an actual refund: on the initial connect (before any
    // recovery) recoveryAttempts is already 0, so "resetting" would be misleading noise
    // in Sentry breadcrumb trails.
    const wasBannerShowing = recoveryExhaustedNotified;
    if (inExhaustedState) {
      notifyReconnect(Number.POSITIVE_INFINITY);
    }
    if (recoveryAttempts > 0) {
      addBreadcrumb({
        category: "nats",
        message: "Connected - resetting recovery attempts to 0",
        level: "info",
        data: { name: options.name },
      });
    }
    recoveryAttempts = 0;
    sentryExhaustedCaptured = false;
    markConnectedThisSession();
    exitExhaustedState();
    if (wasBannerShowing) {
      recoveryExhaustedNotified = false;
      options.onRecovered?.();
    }
  }

  function scheduleRecovery() {
    if (recoveryTimer) {
      return;
    }

    if (recoveryAttempts >= MAX_RECOVERY_ATTEMPTS) {
      handleExhausted();
      return;
    }

    const delay = RECOVERY_BASE_DELAY_MS + Math.random() * RECOVERY_JITTER_MS;
    recoveryTimer = setTimeout(() => {
      recoveryTimer = undefined;
      void rebuildClient({ notify: true, burnBudget: true });
    }, delay);
  }

  // Fires every time scheduleRecovery() finds the fast ladder exhausted (existing semantics).
  // Sentry capture is gated to once-per-cycle to keep volume proportional to user impact
  // instead of looping with each new connection failure. Once gated, decides whether to enter
  // the post-exhaustion machine (slow re-dial) or dead-end exactly as before the switch.
  function handleExhausted() {
    options.onRecoveryExhausted?.();
    if (!sentryExhaustedCaptured) {
      captureException(new Error("NATS recovery exhausted, user has no realtime"), {
        // Network state goes in tags (not extra) so Sentry can split the issue
        // by it: "device was offline" (a blip we shouldn't count as lost realtime)
        // vs "online but path to NATS blocked" (the real signal). See
        // nats-init-failure-experiments.md (C1).
        // everConnectedThisSession/everConnectedOnThisDevice further split the issue into
        // "blip" (transient hiccup) vs "chronically blocked" (WebSocket never works on this
        // device — school middlebox territory), which gates a future fallback-transport track.
        // lastConnectedOnThisDevice/deviceFlagAge (C4.1) correct two biases in that split:
        // a stale "true" (device connected once, long ago) and a storage-wiped "false"
        // (device connected recently but the flag was cleared) — see types.ts for detail.
        tags: {
          component: "RealtimeHook",
          operation: "recoveryExhausted",
          ...getClientNetworkState(),
          everConnectedThisSession: String(everConnectedThisSession),
          everConnectedOnThisDevice: getEverConnectedOnThisDevice(),
          lastConnectedOnThisDevice: getLastConnectedBucket(),
          deviceFlagAge: getDeviceFlagAgeBucket(),
        },
        extra: { recoveryAttempts, name: options.name },
        // Without an explicit fingerprint, Sentry groups by stack trace —
        // splitting one logical event across many issues per app and per release.
        // Collapsing to (event, app name) gives one issue per app.
        fingerprint: ["nats-recovery-exhausted", options.name],
      });
      sentryExhaustedCaptured = true;
    }
    if (!(options.isExhaustedStateMachineEnabled?.() ?? false)) {
      // Feature switch OFF — today's dead-end behavior, unchanged (incl. no auto-dismiss).
      // Also disarm auto-dismiss in case the switch flipped OFF mid-exhaustion-cycle:
      // a later recovery must not fire onRecovered while the feature is off.
      recoveryExhaustedNotified = false;
      return;
    }
    // Only set once the machine is on: this flag arms onRecovered (banner auto-dismiss),
    // which is part of the gated behavior — switch OFF must stay byte-for-byte today's
    // dead-end, where the banner only ever clears via the manual X.
    recoveryExhaustedNotified = true;
    inExhaustedState = true;
    scheduleSlowRetry();
    startPollLoop();
  }

  // Shared-topic bookkeeping reset used by both teardown paths (rebuildClient
  // and close). Everything here resets synchronously so a racing subscriber can
  // immediately build a fresh client without stale registrations.
  function resetSharedClientState() {
    topicClients.clear();
    topicSubscriberCounts.clear();
    currentEntityId = null;
    initPromise = null;
    hadSubscriptionError = false;
    clearTimeout(errorResetTimer);
  }

  // Shared rebuild body for both the fast recovery ladder and the slow post-exhaustion
  // retries. `burnBudget` decides whether this attempt spends the fast-ladder budget
  // (recoveryAttempts) — slow retries must NOT, or every slow tick would re-exhaust
  // instantly. `notify` decides whether to fire the refetch fan-out immediately: the fast
  // ladder always has (today's behavior, unchanged); slow retries defer the fan-out until
  // an actual connect succeeds (handleConnectSuccess/handleReconnectSuccess), because
  // notifying on every failed slow rebuild every ~1-5 min would be a refetch storm.
  async function rebuildClient({ notify, burnBudget }: { notify: boolean; burnBudget: boolean }) {
    const closeGenerationAtStart = closeGeneration;
    addBreadcrumb({
      category: "nats",
      message: burnBudget
        ? `Recovery attempt ${recoveryAttempts + 1}/${MAX_RECOVERY_ATTEMPTS}`
        : `Slow re-dial attempt ${slowRetryAttempts}`,
      level: "info",
      data: { name: options.name },
    });
    const clientBeingClosed = realtimeClient;
    if (clientBeingClosed) {
      try {
        await clientBeingClosed.close();
        // eslint-disable-next-line no-catch-all/no-catch-all -- best-effort close during recovery
      } catch (err) {
        withScope((scope) => {
          scope.setTag("productArea", "shared");
          captureException(err instanceof Error ? err : new Error(String(err)), {
            tags: { component: "RealtimeHook", operation: "recoveryClose" },
          });
        });
      }
      // Only null what we actually closed: close() may have nulled it (and a
      // racing re-login may have built a fresh client) while we were awaiting —
      // nulling unconditionally would orphan that fresh client's socket.
      if (realtimeClient === clientBeingClosed) {
        realtimeClient = null;
      }
    }
    if (closeGeneration !== closeGenerationAtStart) {
      // close() ran while we were awaiting: don't bump the reconnect generation
      // or re-clear state — that would resurrect a client after the session
      // ended. The identity check above can't cover this: after a plain close()
      // with no successor client, realtimeClient is simply null, which is
      // indistinguishable from "never had a client" without the counter.
      return;
    }
    resetSharedClientState();
    connectionExhausted = false;
    if (burnBudget) {
      recoveryAttempts++;
    }

    bumpReconnectGeneration();
    if (notify) {
      // Recovery means the connection was effectively dead long enough to exhaust
      // adapter-level reconnects — pass Infinity so listeners with duration gates
      // always treat this as a real outage and refetch.
      notifyReconnect(Number.POSITIVE_INFINITY);
    }
  }

  // A4 — full-jitter backoff, visible-tab-only, idempotent while a timer is already pending.
  // A failed slow rebuild re-enters scheduleRecovery() (still >= MAX) -> handleExhausted()
  // -> here again with a grown cap; recoveryAttempts stays pinned so the cycle never re-burns
  // the fast-ladder budget.
  function scheduleSlowRetry() {
    if (slowRetryTimer) return;
    if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
    const cap = Math.min(SLOW_RETRY_BASE_MS * 2 ** slowRetryAttempts, SLOW_RETRY_MAX_MS);
    const delay = Math.random() * cap;
    slowRetryTimer = setTimeout(() => {
      slowRetryTimer = undefined;
      // Defensive re-check: if the tab hid after this timer was scheduled, don't rebuild
      // from a hidden tab — the visible re-arm covers the return path.
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      slowRetryAttempts++;
      options.metrics?.increment("nats.exhausted_retry", { outcome: "attempt" });
      void rebuildClient({ notify: false, burnBudget: false });
    }, delay);
  }

  // E1 — separate timer from the slow retry: a poll refetch doesn't imply the socket
  // works, and a slow-retry failure shouldn't delay data freshness. Visible-tab-only,
  // reschedules itself while exhausted. Skips the fan-out (not the reschedule) when there
  // are no refetch listeners registered — the tick still needs to keep recurring so a
  // listener added later during the same exhausted stretch gets picked up. Only reached
  // when the machine switch is on (handleExhausted gate); the poll switch is re-read on
  // every schedule so turning it off stops the loop at the next tick (kill switch).
  function startPollLoop() {
    if (pollTimer) return;
    if (!(options.isExhaustedPollEnabled?.() ?? false)) return;
    if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
    const delay = POLL_INTERVAL_MS + Math.random() * POLL_JITTER_MS;
    pollTimer = setTimeout(() => {
      pollTimer = undefined;
      // Defensive re-check: if the tab hid after this timer was scheduled, don't fan out or
      // keep polling from a hidden tab — the visible re-arm restarts the loop on return.
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      if (refetchListeners.size > 0) {
        options.metrics?.increment("nats.exhausted_poll");
        notifyReconnect(Number.POSITIVE_INFINITY);
      }
      if (inExhaustedState) startPollLoop();
    }, delay);
  }

  // Called on any successful connect while the post-exhaustion machine is running.
  function exitExhaustedState() {
    if (!inExhaustedState) return;
    inExhaustedState = false;
    slowRetryAttempts = 0;
    clearTimeout(slowRetryTimer);
    slowRetryTimer = undefined;
    clearTimeout(pollTimer);
    pollTimer = undefined;
    options.metrics?.increment("nats.exhausted_retry", { outcome: "connected" });
  }

  function handleConnectionExhausted() {
    connectionExhausted = true;
    addBreadcrumb({
      category: "nats",
      message: "Connection exhausted, scheduling recovery",
      level: "warning",
      data: { recoveryAttempts, name: options.name },
    });
    options.onConnectionExhausted?.();
    scheduleRecovery();
  }

  function handleInitError() {
    connectionExhausted = true;
    addBreadcrumb({
      category: "nats",
      message: "Initialization failed, scheduling recovery",
      level: "warning",
      data: { recoveryAttempts, name: options.name },
    });
    options.onInitError?.();
    scheduleRecovery();
  }

  // A5 — shared re-arm for both the visibility and online paths: gives a returning tab a
  // fresh FAST ladder immediately instead of waiting on the slow loop's own backoff.
  // Does NOT reset recoveryExhaustedNotified — the banner is still showing until a real
  // connect fires onRecovered, and a re-arm that then connects must still auto-dismiss it.
  function rearmRecovery() {
    recoveryAttempts = 0;
    sentryExhaustedCaptured = false;
    slowRetryAttempts = 0;
    clearTimeout(slowRetryTimer);
    slowRetryTimer = undefined;
    inExhaustedState = false;
    clearTimeout(pollTimer);
    pollTimer = undefined;
    scheduleRecovery();
  }

  function handleVisibilityChange() {
    if (document.visibilityState === "visible" && (connectionExhausted || inExhaustedState)) {
      rearmRecovery();
    }
  }

  // The slow loop itself is the kiosk fix for always-visible tabs; this listener covers
  // tabs that regain network without a visibility change (A1's gate on navigator.onLine
  // stays dead — this is an accelerant only, false negatives cost nothing).
  function handleOnline() {
    if (connectionExhausted || inExhaustedState) {
      rearmRecovery();
    }
  }

  // Called on every (re)build of the shared client, not eagerly at hook
  // creation: the handlers are inert until a client exists to set the
  // exhaustion flags, and close() (e.g. logout) removes them — so a client
  // rebuilt after close() (re-login without a reload) must regain them.
  // addEventListener dedupes identical handler references, so re-entry is a no-op.
  function registerRecoveryListeners() {
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("online", handleOnline);
    }
  }

  // Clean up listeners on HMR to prevent leaks
  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);
    });
  }

  const onSubscriptionError = options.onError
    ? () => {
        hadSubscriptionError = true;
        clearTimeout(errorResetTimer);
        errorResetTimer = setTimeout(() => {
          hadSubscriptionError = false;
        }, ERROR_FLAG_TTL_MS);
        options.onError!();
      }
    : undefined;

  function getSharedRealtimeClient(): RealtimeClient {
    if (!realtimeClient) {
      registerRecoveryListeners();
      // Stamp flag_created_at on the first build of this hook's client (fires exactly once
      // per hook lifetime), so deviceFlagAge is meaningful even for devices that never
      // successfully connect — not just devices that reach markEverConnectedOnThisDevice().
      ensureFlagCreatedAt();
      if (options.mockAdapter) {
        options.mockAdapter.onConnectionExhausted = handleConnectionExhausted;
        options.mockAdapter.onInitError = handleInitError;
        options.mockAdapter.onReconnect = handleReconnectSuccess;
        options.mockAdapter.onConnect = handleConnectSuccess;
        realtimeClient = new RealtimeClient(options.mockAdapter, {
          onSubscriptionError,
          metrics: options.metrics,
        });
      } else {
        // Create a shared RealtimeClient that will be reused for all topics
        const adapter = new NatsAdapter({
          server: options.serverUrl,
          name: options.name,
          onConnectionExhausted: handleConnectionExhausted,
          onSubscriptionError,
          onInitError: handleInitError,
          onReconnect: handleReconnectSuccess,
          onConnect: handleConnectSuccess,
          metrics: options.metrics,
        });
        realtimeClient = new RealtimeClient(adapter, {
          onSubscriptionError,
          metrics: options.metrics,
        });
      }
    }
    return realtimeClient;
  }

  function getClient(topicOrEntityId: string): IRealtimeUserClient {
    // Check if we already have a client for this topic
    let client = topicClients.get(topicOrEntityId);
    if (client) {
      return client;
    }

    // Detect session swaps: if there was a subscription error and the user
    // topic (user.{id}) is changing, that indicates a session change mid-error.
    // Only check user topics (not custom topics like classroom.*).
    const isUserTopic = !topicOrEntityId.includes(".");
    if (hadSubscriptionError && isUserTopic && currentEntityId && currentEntityId !== topicOrEntityId) {
      captureException(new Error("NATS session identity changed after subscription error"), {
        extra: {
          previousEntityId: currentEntityId,
          newEntityId: topicOrEntityId,
        },
        tags: { component: "RealtimeHook", operation: "sessionSwapDetected" },
      });
      hadSubscriptionError = false;
      clearTimeout(errorResetTimer);
    }

    // Create a new UserClient for this topic, using the shared RealtimeClient
    client = new RealtimeUserClient(topicOrEntityId, getSharedRealtimeClient());
    topicClients.set(topicOrEntityId, client);

    if (isUserTopic) {
      currentEntityId = topicOrEntityId;
    }
    initPromise = null;
    return client;
  }

  function useRealtime<T extends RealtimePayload["action"]>(
    config: { entityId: string | undefined; enabled: boolean },
    actions: T[],
    handler: RealtimeMessageHandler<Extract<RealtimePayload, { action: T }>>,
  ) {
    const handlerRef = useRef(handler);
    const generation = useSyncExternalStore(subscribeReconnect, getReconnectSnapshot);

    useLayoutEffect(() => {
      handlerRef.current = handler;
    });

    const actionsKey = actions.join(",");

    useEffect(() => {
      if (!config.enabled || !config.entityId) {
        return;
      }

      const stableHandler: typeof handler = (message) => {
        void handlerRef.current(message);
      };

      const topic = config.entityId;
      const currentClient = getClient(topic);
      topicSubscriberCounts.set(topic, (topicSubscriberCounts.get(topic) ?? 0) + 1);

      initPromise ??= currentClient.init();

      let isUnmounted = false;

      const subscribe = async () => {
        await initPromise;
        if (isUnmounted) {
          return;
        }
        await currentClient.subscribe(actions, stableHandler);
      };

      subscribe().catch((err) => {
        if (err instanceof NatsError && err.isInitFailure) {
          return;
        }
        captureException(err);
      });

      return () => {
        isUnmounted = true;
        // Only clean up if this client is still the registered one. rebuildClient calls
        // topicClients.clear() before bumpReconnectGeneration(), so by the time this cleanup
        // runs the old RealtimeClient is already closed (handlers cleared) — calling unsubscribe
        // on it would throw "No handlers found for topic during unsubscribe". topicSubscriberCounts
        // is also cleared by recovery, so the remaining logic would be all no-ops (or worse,
        // could clobber a newly-registered client for the same topic).
        if (topicClients.get(topic) === currentClient) {
          currentClient.unsubscribe(stableHandler).catch((err) => captureException(err));
          const remaining = (topicSubscriberCounts.get(topic) ?? 1) - 1;
          if (remaining <= 0) {
            topicSubscriberCounts.delete(topic);
            topicClients.delete(topic);
            currentClient.close().catch((err) => captureException(err));
          } else {
            topicSubscriberCounts.set(topic, remaining);
          }
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps -- generation triggers re-subscription after recovery
    }, [actionsKey, config.enabled, config.entityId, generation]);
  }

  let pendingInit: Promise<void> | null = null;

  function publish(topic: string, data: Record<string, unknown>): void {
    const client = getSharedRealtimeClient();
    if (pendingInit) {
      void pendingInit.then(() => client.publish(topic, data));
      return;
    }
    client.publish(topic, data);
  }

  async function init(): Promise<void> {
    const client = getSharedRealtimeClient();
    pendingInit = client.init();
    try {
      await pendingInit;
    } finally {
      pendingInit = null;
    }
  }

  async function close(): Promise<void> {
    closeGeneration++;
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    }
    if (typeof window !== "undefined") {
      window.removeEventListener("online", handleOnline);
    }
    clearTimeout(recoveryTimer);
    recoveryTimer = undefined;
    clearTimeout(reconnectNotifyTimer);
    reconnectNotifyTimer = undefined;
    // A stale coalesced value (often Infinity from a recovery notify) must not
    // survive into the next session — it would turn the first sub-second flap
    // after re-login into a "full outage" refetch fan-out.
    pendingDisconnectMs = 0;
    clearTimeout(slowRetryTimer);
    slowRetryTimer = undefined;
    clearTimeout(pollTimer);
    pollTimer = undefined;
    connectionExhausted = false;
    recoveryAttempts = 0;
    sentryExhaustedCaptured = false;
    inExhaustedState = false;
    slowRetryAttempts = 0;
    recoveryExhaustedNotified = false;
    everConnectedThisSession = false;
    pendingInit = null;
    // Reset ALL shared state synchronously before awaiting the adapter close:
    // a caller racing this close (e.g. a fast re-login subscribing while a
    // fire-and-forget logout close is still in flight) must build a fresh
    // client, not attach to one mid-teardown or have its registrations
    // clobbered by a post-await clear.
    const clientToClose = realtimeClient;
    realtimeClient = null;
    resetSharedClientState();
    // Note: close() intentionally does NOT bump reconnectGeneration — any
    // still-mounted useRealtime subscribers stay unsubscribed until their own
    // deps change (callers close on logout, where the tree unmounts anyway).
    if (clientToClose) {
      await clientToClose.close();
    }
  }

  return {
    useRealtime,
    publish,
    init,
    close,
    subscribeReconnect,
    getReconnectSnapshot,
    addReconnectListener,
  };
}
